import React, { memo, useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { READER_THEMES } from "utils/themes";
import EbookSearchMenu from "components/ebook-reader/components/search-menu";


const MIN_LENGTH_TO_SEARCH = 3;

const ContentStatus = Object.freeze({
    default: 'empty',
    content: 'content',
    noResults: 'no results',
});


const SearchMenu = memo((props) => {
    const { pdf, controlBtnProps, updateEBookDataAction } = props;
    const { t } = useTranslation();

    const [inputValue, setInputValue] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [searchResults, setSearchResults] = useState([]);
    const [contentStatus, setContentStatus] = useState(ContentStatus.default);
    const [extractedTextByPage, setExtractedTextByPage] = useState([]);

    const messages = useMemo(() => ({
        [ContentStatus.default]: {
            title: t("ebook_search_results_empty_title"),
            message: t("ebook_search_results_empty_message"),
        },
        [ContentStatus.noResults]: {
            title: t("ebook_search_no_results_title"),
            message: t("ebook_search_no_results_message"),
        },
    }), []);

    /** disable search for too small terms **/
    const isSearchEnabled = useMemo(
        () => inputValue.length >= MIN_LENGTH_TO_SEARCH,
        [inputValue]
    );

    /** keep input value state **/
    const handleInputChange = useCallback(
    (event) => {
        const value = event.currentTarget.value;
        setInputValue(value);
        if (value === '') {
            clearSearchInput();
        }
    }, [setInputValue]);

    /** clear input **/
    const clearSearchInput = useCallback(
    () => {
        setInputValue('');
        setSearchResults([]);
        setIsLoading(false);
        setContentStatus(ContentStatus.default);
        updateEBookDataAction({ searchHighlight: null });
    }, []);

    /**
     * Navigate to the page of the selected result. For pdf, we assume that href is the page number.
     * **/
    const onResultClick = useCallback((result) => async () => {
        updateEBookDataAction({ currentPage: parseInt(result.href) });
    }, [searchResults]);

    /** extract text from pdf, store by page (index in array + 1) **/
    useEffect(() => {
        if (!pdf) return setExtractedTextByPage([]);

        setIsLoading(true);

        const extractPageText = async (pageNum) => {
            const page = await pdf.getPage(pageNum);
            const textContent = await page.getTextContent();
            return textContent.items.map(item => item.str).join(' ');
        };

        Promise.all(
            Array.from(
                { length: pdf.numPages },
                (_, i) => extractPageText(i + 1)
            )
        ).then(pageTexts => {
            setExtractedTextByPage(pageTexts);
            setIsLoading(false);
        });
    }, [pdf]);

    /** search in extracted text **/
    const getLocalSearchResults = useCallback(() => {
        return new Promise((resolve, reject) => {
            if (!inputValue.trim() || !extractedTextByPage.length) {
                resolve([]);
                return ;
            }

            const regex = new RegExp(`\\b${inputValue}\\b`, 'gi');
            const contextLength = 50; // Number of characters to include before and after the match

            resolve(extractedTextByPage.flatMap((pageText, pageIndex) => {
                const matches = [...pageText.matchAll(regex)];
                return matches.map(match => {
                    const startIndex = Math.max(0, match.index - contextLength);
                    const endIndex = Math.min(pageText.length, match.index + match[0].length + contextLength);

                    return {
                        uuid: match.index,
                        href: pageIndex + 1,
                        title: t('generic_format_page', { arg: pageIndex + 1 }),
                        textBefore: pageText.slice(startIndex, match.index),
                        textMatch: match[0],
                        textAfter: pageText.slice(match.index + match[0].length, endIndex),
                    };
                });
            }));
        });
    }, [inputValue, extractedTextByPage]);


    /** search **/
    const handleSearch = useCallback( async (e) => {
        e.preventDefault();
        setContentStatus(ContentStatus.content);
        setSearchResults([]);
        if (!isSearchEnabled) {
            setContentStatus(ContentStatus.default);
            return;
        }

        setIsLoading(true);

        getLocalSearchResults()
        .then(results => {
            setIsLoading(false);
            setSearchResults(results);
            updateEBookDataAction({ searchHighlight: inputValue });
            if (results && results.length === 0) {
                setContentStatus(ContentStatus.noResults);
            }
        })
        .catch(() => {
            setIsLoading(false);
            setSearchResults([]);
            updateEBookDataAction({ searchHighlight: null });
            setContentStatus(ContentStatus.noResults);
        });
    }, [inputValue, isSearchEnabled, pdf, extractedTextByPage, getLocalSearchResults]);

    return (
        <EbookSearchMenu
            activeTheme={READER_THEMES.light}
            controlBtnProps={controlBtnProps}
            showSearchMenu={true}
            showSearchTypeTabs={false}
            searchButtonEnabled={isSearchEnabled}
            globalSearchEnabled={true}
            handleSearch={handleSearch}
            onResultClick={onResultClick}
            afterInputCleared={clearSearchInput}
            onInputChange={handleInputChange}
            searchResults={searchResults}
            isLoading={isLoading}
            contentStatus={contentStatus}
            inputValue={inputValue}
            message={messages[contentStatus]}
        />
    )
});

SearchMenu.propTypes = {
    pdf: PropTypes.object,
    controlBtnProps: PropTypes.shape({
        className: PropTypes.string,
        iconClassName: PropTypes.string,
    }),
    updateEBookDataAction: PropTypes.func.isRequired,
};

export default SearchMenu;
